import { useCallback } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { v4 as uuidv4 } from 'uuid';

import { COUNTER_PARTY_STATUS } from '#client/constants/payees';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useToastContext from '#client/hooks/useToastContext';
import { UPDATE_COUNTER_PARTY_STATUS_MUTATION } from '#client/mutations';

const useUpdatePayee = () => {
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const { createSuccessToast, createErrorToast } = useToastContext();
  const captureException = useCaptureException();
  const [updateCounterParty, { loading }] = useMutation(UPDATE_COUNTER_PARTY_STATUS_MUTATION, {
    noThrow: true
  });

  const updatePayeeStatus = useCallback(
    async ({ id, name, status, bankingAccountId, handleClose, resetPagination }) => {
      try {
        // Retrieve customer token
        const customerToken = await getCustomerToken();

        // If there's no customer token, close the modal and return
        if (!customerToken) {
          handleClose();
          return;
        }
        // Update the payee status
        const { error: updatePayeeError } = await updateCounterParty({
          variables: {
            input: {
              counterpartyId: id,
              status,
              customerToken,
              bankingAccountId,
              idempotencyKey: uuidv4()
            }
          },
          skip: loading
        });

        // Handle errors and show a toast
        if (updatePayeeError) {
          // Show toast error
          createErrorToast({
            title: 'Payee was not updated',
            description: 'There was an error updating this payee. Please try again.',
            error: updatePayeeError
          });
        } else {
          // Show success toast and close modal
          createSuccessToast({
            title: `${name} has been ${
              status === COUNTER_PARTY_STATUS.ARCHIVED ? 'archived' : 'unarchived'
            }`,
            description: `${
              status === COUNTER_PARTY_STATUS.ARCHIVED
                ? `You can still access your transaction history with ${name}`
                : `You can send money to ${name} again`
            }`
          });

          // Reset pagination after successful update
          resetPagination();
        }
      } catch (error) {
        // Handle unexpected errors
        captureException(new Error('Error updating payee status'));
      }
    },
    [
      getCustomerToken,
      updateCounterParty,
      loading,
      createErrorToast,
      createSuccessToast,
      captureException
    ]
  );

  return {
    updatePayeeStatus,
    loading
  };
};

export default useUpdatePayee;
